import React from "react";
import { useBookingContext } from "../context/BookingContext";
import Modal from "react-responsive-modal";
import Zone1 from "./Zone1";
import Zone2 from "./Zone2";
import moment from "moment";
import { useTranslation } from "react-i18next";

function OutOfTownModal() {
  const { setOpenOutOfTownModal, dateUsage, openZone1, setOpenZone1, openZone2, setOpenZone2 } = useBookingContext();
  const datas = dateUsage?.outOfTown;
  const zone1 = datas?.filter((i) => i?.zone?.includes(1));
  const zone2 = datas?.filter((i) => i?.zone?.includes(2));
  const { t } = useTranslation();

  return (
    <div className="w-full max-w-3xl p-6 bg-white rounded-3xl md:p-8">
      {/* Header */}
      <div className="mb-6">
        <h3 className="mb-2 text-xl font-bold md:text-2xl text-slate-900">{t("select_date")}</h3>
        <p className="text-sm text-slate-600">Choose zones for out of town usage</p>
      </div>

      {/* Zone Cards Grid */}
      <div className="grid grid-cols-1 gap-6 mb-6 md:grid-cols-2">
        {/* Zone 1 Card */}
        <div className="overflow-hidden transition-all duration-300 border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-white rounded-2xl hover:border-blue-300">
          <div className="p-6">
            {/* Zone Header */}
            <div className="flex items-center gap-3 mb-4">
              <div className="flex items-center justify-center w-12 h-12 shadow-lg bg-gradient-to-br from-blue-400 to-blue-500 rounded-xl">
                <span className="text-xl font-bold text-white">1</span>
              </div>
              <div>
                <h4 className="text-xl font-bold text-slate-900">{t("zone_1")}</h4>
              </div>
            </div>

            {/* Zone Description */}
            <p className="mb-4 text-sm leading-relaxed text-slate-600">{t("kerobokan_etc")}</p>

            {/* Selected Dates */}
            {zone1?.length > 0 && (
              <div className="p-4 mb-4 bg-white border border-blue-200 rounded-xl">
                <p className="mb-2 text-xs font-semibold uppercase text-slate-600">Selected Dates:</p>
                <div className="flex flex-wrap gap-2">
                  {zone1.map((v, idx) => (
                    <span
                      key={idx}
                      className="inline-flex items-center gap-1.5 px-3 py-1 bg-blue-100 text-blue-700 rounded-lg text-sm font-semibold border border-blue-200"
                    >
                      <i className="text-xs fa-solid fa-calendar-check"></i>
                      {moment(v.date).format("DD MMM")}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Add Button */}
            <button
              onClick={() => setOpenZone1(true)}
              className="w-full px-5 py-3 font-semibold text-white transition-all duration-300 bg-blue-500 shadow-lg hover:bg-blue-600 rounded-xl shadow-blue-500/30"
            >
              <i className="mr-2 fa-solid fa-plus"></i>
              {zone1?.length > 0 ? t("change") : t("add")}
            </button>
          </div>
        </div>

        {/* Zone 2 Card */}
        <div className="overflow-hidden transition-all duration-300 border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-white rounded-2xl hover:border-purple-300">
          <div className="p-6">
            {/* Zone Header */}
            <div className="flex items-center gap-3 mb-4">
              <div className="flex items-center justify-center w-12 h-12 shadow-lg bg-gradient-to-br from-purple-400 to-purple-500 rounded-xl">
                <span className="text-xl font-bold text-white">2</span>
              </div>
              <div>
                <h4 className="text-xl font-bold text-slate-900">{t("zone_2")}</h4>
              </div>
            </div>

            {/* Zone Description */}
            <p className="mb-4 text-sm leading-relaxed text-slate-600">{t("karangasem_etc")}</p>

            {/* Selected Dates */}
            {zone2?.length > 0 && (
              <div className="p-4 mb-4 bg-white border border-purple-200 rounded-xl">
                <p className="mb-2 text-xs font-semibold uppercase text-slate-600">Selected Dates:</p>
                <div className="flex flex-wrap gap-2">
                  {zone2.map((v, idx) => (
                    <span
                      key={idx}
                      className="inline-flex items-center gap-1.5 px-3 py-1 bg-purple-100 text-purple-700 rounded-lg text-sm font-semibold border border-purple-200"
                    >
                      <i className="text-xs fa-solid fa-calendar-check"></i>
                      {moment(v.date).format("DD MMM")}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Add Button */}
            <button
              onClick={() => setOpenZone2(true)}
              className="w-full px-5 py-3 font-semibold text-white transition-all duration-300 bg-purple-500 shadow-lg hover:bg-purple-600 rounded-xl shadow-purple-500/30"
            >
              <i className="mr-2 fa-solid fa-plus"></i>
              {zone2?.length > 0 ? t("change") : t("add")}
            </button>
          </div>
        </div>
      </div>

      {/* Info Note */}
      <div className="flex items-start gap-3 p-4 mb-6 border-2 bg-amber-50 border-amber-200 rounded-xl">
        <i className="fa-solid fa-circle-info text-amber-600 text-lg mt-0.5 flex-shrink-0"></i>
        <p className="text-sm leading-relaxed text-amber-900">{t("this_rate_valid")}</p>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col gap-3 pt-4 border-t sm:flex-row border-slate-200">
        <button
          onClick={() => setOpenOutOfTownModal(false)}
          className="flex-1 px-6 py-3 font-semibold transition-all duration-300 border bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl border-slate-200"
        >
          <i className="mr-2 fa-solid fa-xmark"></i>
          {t("close")}
        </button>
        <button
          onClick={() => setOpenOutOfTownModal(false)}
          className="flex-1 px-6 py-3 bg-[#f5272b] hover:bg-red-700 text-white font-semibold rounded-xl transition-all duration-300 shadow-lg shadow-[#f5272b]/30"
        >
          <i className="mr-2 fa-solid fa-check"></i>
          {t("save")}
        </button>
      </div>

      {/* Zone Modals */}
      {openZone1 && (
        <Modal
          open={openZone1}
          showCloseIcon={false}
          onClose={() => {}}
          styles={{
            root: { zIndex: 9999999992 },
          }}
        >
          <Zone1 />
        </Modal>
      )}
      {openZone2 && (
        <Modal
          open={openZone2}
          showCloseIcon={false}
          onClose={() => {}}
          styles={{
            root: { zIndex: 9999999992 },
          }}
        >
          <Zone2 />
        </Modal>
      )}
    </div>
  );
}

export default OutOfTownModal;
